import React, { useEffect, useRef } from "react"
import { createRoot } from "react-dom/client"
import anime from "animejs"
import classNames from "classnames"
import { noop } from "lodash"

import styles from "./index.module.less"

interface IProps {
    visible?: boolean
    afterClose?: () => void
    children?: React.ReactNode
    className?: string
}

const OriginalToast: React.FC<IProps> = (props) => {
    const { visible = false, afterClose = noop, className, children } = props

    const node = useRef<HTMLDivElement>(null)

    useEffect(() => {
        anime({
            targets: node.current,
            opacity: visible ? [0, 1] : [1, 0],
            duration: 300,
            easing: "cubicBezier(0.25, 0.1, 0.25, 1)",
            complete: () => {
                if (!visible) {
                    afterClose()
                }
            },
        })
    }, [visible])

    return (
        <div ref={node} className={classNames(styles.toast, className)}>
            <div className={styles.content}>{children}</div>
        </div>
    )
}

interface IToastOptions {
    duration?: number
    content?: React.ReactNode
    className?: string
}

function message(opts: IToastOptions) {
    const { duration = 2000, content, className } = opts

    const div = document.createElement("div")
    document.body.appendChild(div)
    const root = createRoot(div)

    function render({
        content,
        ...props
    }: IProps & { content?: React.ReactNode }) {
        setTimeout(() => {
            root.render(<OriginalToast {...props}>{content}</OriginalToast>)
        })
    }

    render({
        visible: true,
        content,
        className,
    })

    if (duration > 0) {
        setTimeout(() => {
            render({
                visible: false,
                content,
                afterClose: () => {
                    root.unmount()
                    if (div.parentNode) {
                        div.parentNode.removeChild(div)
                    }
                },
            })
        }, duration)
    }
}

interface ToastStaticFunctions {
    message: (opts: IToastOptions) => void
}

type Toast = typeof OriginalToast & ToastStaticFunctions

const Toast = OriginalToast as Toast
Toast.message = message

export default Toast
